import { __assign, __extends } from "tslib";
/**
 *  @internal
 *  @module Resources
 */
import { EventEmitter } from "events";
import { fromError } from "../errors/parser.js";
import { PathParameterError } from "../errors/PathParameterError.js";
import { RequestParameterError } from "../errors/RequestParameterError.js";
import { isBlob, missingKeys, toSnakeCase } from "../utils/object.js";
/**
 * Returns a path with pathParams filled into `:paramName`.
 *
 * If the path is wrapped in `()`, the path within will
 * be omitted if one or more required params are missing.
 *
 * e.g.
 * ```
 * path = "(/merchant/:merchantId/store/:storeId)/platforms"
 * pathParams = { merchantId: "123" }
 * // result: "/platforms"
 *
 * path = "(/merchant/:merchantId/store/:storeId)/platforms"
 * pathParams = { merchantId: "123", storeId: "456" }
 * // result: "merchant/123/store/456/platforms"
 * ```
 *
 * @param path The full path to compile, e.g. `(/merchant/:merchantId)/store/:storeId`
 * @param pathParams Object of params to fill into the path, e.g. `{ merchantId: "abc" }`
 */
function compilePath(path, pathParams) {
    return path
        .replace(/\((\w|:|\/)+\)/gi, function (o) {
        var part = o.replace(/:(\w+)/gi, function (s, p) {
            return pathParams[p] || s;
        });
        return part.indexOf(":") === -1 ? part.replace(/\(|\)/g, "") : "";
    })
        .replace(/:(\w+)/gi, function (s, p) { return pathParams[p] || s; });
}
var getObjectType = function (data) {
    switch (true) {
        case data === null:
            return "null";
        case Array.isArray(data):
            return "array";
        case isBlob(data):
            return "blob";
        default:
            return typeof data;
    }
};
var Resource = /** @class */ (function (_super) {
    __extends(Resource, _super);
    function Resource(api) {
        var _this = _super.call(this) || this;
        _this.api = api;
        _this.on("newListener", function (event, listener) {
            if (event === "newListener" || event === "removeListener") {
                return;
            }
            api.on(event, listener);
        });
        _this.on("removeListener", function (event, listener) { return api.removeListener(event, listener); });
        return _this;
    }
    Resource.prototype.defineRoute = function (method, path, required, requireAuth, acceptType, keyFormatter, ignoreKeys) {
        if (required === void 0) { required = []; }
        if (requireAuth === void 0) { requireAuth = true; }
        if (keyFormatter === void 0) { keyFormatter = toSnakeCase; }
        if (ignoreKeys === void 0) { ignoreKeys = ["metadata"]; }
        var api = this.api;
        return function route(originalData, callback, auth, pathParams) {
            if (pathParams === void 0) { pathParams = {}; }
            /**
             * Sanitises and ensures that the data is recreated as an object with default prototypes.
             * In rare cases if the data was created with `Object.create(null)` (no prototypes),
             * this will cause the data to be sent as `WebKitFormBoundary` instead of regular JSON object.
             *
             * Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/create#custom_and_null_objects
             */
            var data = getObjectType(originalData) === "object" ? __assign({}, originalData) : originalData;
            var url = compilePath(path, pathParams);
            var missingPathParams = (url.match(/:([a-z]+)/gi) || []).map(function (m) { return m.replace(":", ""); });
            var missingParams = missingKeys(data, required);
            var err;
            if (missingPathParams.length > 0) {
                err = fromError(new PathParameterError(missingPathParams[0]));
                callback === null || callback === void 0 ? void 0 : callback(err);
                return Promise.reject(err);
            }
            if (missingParams.length > 0) {
                err = fromError(new RequestParameterError(missingParams[0]));
                callback === null || callback === void 0 ? void 0 : callback(err);
                return Promise.reject(err);
            }
            // TODO: Have a look as this cast, the response can be other objects such as string, Blob or FormData
            return api.send(method, url, data, auth, callback, requireAuth, acceptType, keyFormatter, ignoreKeys);
        };
    };
    return Resource;
}(EventEmitter));
export { Resource };
//# sourceMappingURL=Resource.js.map